/**
 * Editor for the FOR-loop step type: variable name + iteration-mode picker +
 * either a field-source slot (with skip-values chips) or a literal-values
 * chip list. Renders a nested `<StepList>` for `step.body` so loops nest
 * recursively, sharing the same drag/drop and Add-Step plumbing.
 */
import React, { useState } from "react"
import { Button, Input, Modal, Radio } from "antd"
import type { RadioChangeEvent } from "antd"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faExpand, faXmark } from "@fortawesome/free-solid-svg-icons"
import { LoopStep, StepIssue } from "stores/queryBuilderStore"
import SingleFieldSlot from "./SingleFieldSlot.component"
import StepList from "./StepList.component"
import { LITERAL_EXPAND_THRESHOLD } from "./outlineUtils"

interface LoopStepBodyProps {
  step: LoopStep
  scope: string[]
  compact: boolean
  expandedIds: Set<string>
  toggleExpanded: (id: string) => void
  allIssues: Map<string, StepIssue[]>
  onChangeVariable: (variable: string) => void
  onChangeKind: (kind: "field" | "literal") => void
  onChangeFieldSource: (source: string) => void
  onAddExcludedValue: (value: string) => void
  onRemoveExcludedValue: (value: string) => void
  onAddLiteralValue: (value: string) => void
  onRemoveLiteralValue: (value: string) => void
  onSetLiteralValues: (values: string[]) => void
}

const LoopStepBody: React.FC<LoopStepBodyProps> = ({
  step,
  scope,
  compact,
  expandedIds,
  toggleExpanded,
  allIssues,
  onChangeVariable,
  onChangeKind,
  onChangeFieldSource,
  onAddExcludedValue,
  onRemoveExcludedValue,
  onAddLiteralValue,
  onRemoveLiteralValue,
  onSetLiteralValues,
}) => {
  const [excludeDraft, setExcludeDraft] = useState("")
  const [literalDraft, setLiteralDraft] = useState("")
  const [expandOpen, setExpandOpen] = useState(false)
  const [expandedDraft, setExpandedDraft] = useState("")

  const childScope = step.variable.trim()
    ? [...scope, step.variable.trim()]
    : scope

  const handleSubmitLiteral = () => {
    const trimmed = literalDraft.trim()
    if (!trimmed) return
    onAddLiteralValue(trimmed)
    setLiteralDraft("")
  }

  const openExpand = () => {
    if (step.iteration.kind !== "literal") return
    setExpandedDraft(step.iteration.values.join("\n"))
    setExpandOpen(true)
  }
  const commitExpand = () => {
    const lines = expandedDraft
      .split(/\r?\n/)
      .map((s) => s.trim())
      .filter(Boolean)
    onSetLiteralValues(Array.from(new Set(lines)))
    setExpandOpen(false)
  }

  return (
    <>
      <div className="qc-loop__header-row">
        <span className="qc-loop__keyword qc-mono">FOR</span>
        <input
          className="qc-loop__var-input qc-mono"
          type="text"
          value={step.variable}
          onChange={(e) => onChangeVariable(e.target.value)}
          placeholder="ITEM"
          aria-label="Loop variable"
        />
        <span className="qc-loop__keyword">in</span>
        <Radio.Group
          value={step.iteration.kind}
          onChange={(e: RadioChangeEvent) => onChangeKind(e.target.value)}
          size="small"
        >
          <Radio.Button value="field">Field values</Radio.Button>
          <Radio.Button value="literal">A list</Radio.Button>
        </Radio.Group>
      </div>

      {step.iteration.kind === "field" ? (
        <>
          <div className="qc-step__field-row">
            <SingleFieldSlot
              value={step.iteration.source}
              onChange={onChangeFieldSource}
              placeholder="Drag the field whose answer values drive the loop"
            />
          </div>
          <div className="qc-step__field-row">
            <span className="qc-step__field-label">Skip values:</span>
            <div className="qc-loop__literals">
              {/* Defensive `?? []` guards against state that bypassed the
                  normaliser (very old persisted state read before migrate
                  ran, edge-case race after a hot reload, etc.). */}
              {(step.iteration.excludedValues ?? []).map((v) => (
                <span
                  key={v}
                  className="qc-chip qc-mono qc-loop__literal-chip"
                >
                  {v}
                  <button
                    type="button"
                    className="qc-chip__remove"
                    aria-label={`Don't skip ${v}`}
                    onClick={() => onRemoveExcludedValue(v)}
                  >
                    <FontAwesomeIcon icon={faXmark} />
                  </button>
                </span>
              ))}
              <input
                className="qc-loop__literal-input qc-mono"
                type="text"
                value={excludeDraft}
                onChange={(e) => setExcludeDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault()
                    const trimmed = excludeDraft.trim()
                    if (trimmed) {
                      onAddExcludedValue(trimmed)
                      setExcludeDraft("")
                    }
                  }
                }}
                onBlur={() => {
                  const trimmed = excludeDraft.trim()
                  if (trimmed) {
                    onAddExcludedValue(trimmed)
                    setExcludeDraft("")
                  }
                }}
                placeholder="None, Other, Refused…"
                aria-label="Add value to skip"
              />
            </div>
          </div>
        </>
      ) : (
        <>
          <div className="qc-step__field-row">
            <div className="qc-loop__literals">
              {step.iteration.values.map((v) => (
                <span key={v} className="qc-chip qc-mono qc-loop__literal-chip">
                  {v}
                  <button
                    type="button"
                    className="qc-chip__remove"
                    aria-label={`Remove ${v}`}
                    onClick={() => onRemoveLiteralValue(v)}
                  >
                    <FontAwesomeIcon icon={faXmark} />
                  </button>
                </span>
              ))}
              <input
                className="qc-loop__literal-input qc-mono"
                type="text"
                value={literalDraft}
                onChange={(e) => setLiteralDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault()
                    handleSubmitLiteral()
                  }
                }}
                onBlur={handleSubmitLiteral}
                placeholder="Type a value, press Enter"
                aria-label="Add literal value"
              />
              {step.iteration.values.length >= LITERAL_EXPAND_THRESHOLD && (
                <Button
                  icon={<FontAwesomeIcon icon={faExpand} />}
                  onClick={openExpand}
                >
                  Edit list
                </Button>
              )}
            </div>
          </div>
          <Modal
            title={`Edit values for FOR ${step.variable || "loop"}`}
            open={expandOpen}
            onOk={commitExpand}
            onCancel={() => setExpandOpen(false)}
            okText="Save values"
            cancelText="Cancel"
            destroyOnClose
          >
            <p style={{ marginTop: 0, color: "var(--qc-ink-muted)" }}>
              One value per line. Blank lines and duplicates are dropped.
            </p>
            <Input.TextArea
              value={expandedDraft}
              onChange={(e) => setExpandedDraft(e.target.value)}
              rows={10}
              autoFocus
            />
          </Modal>
        </>
      )}

      <StepList
        steps={step.body}
        parentLoopId={step.id}
        scope={childScope}
        compact={compact}
        expandedIds={expandedIds}
        toggleExpanded={toggleExpanded}
        allIssues={allIssues}
      />
    </>
  )
}

export default LoopStepBody
